import {Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {CreBankAccountDataService} from 'src/app/services/gp/cre-bank-account-data.service';
import {UnitBankAccountDataService} from 'src/app/services/shared/unit-bank-account-data.service';
import {UnitAchLimits} from 'src/app/shared/models/unit-bank-account/UnitAchLimits.model';
import {UnitBankAccountBalanceReqRes} from 'src/app/shared/models/gp/UnitBankAccountBalanceReqRes.model';
import {UnitBankAccountReqRes} from 'src/app/shared/models/gp/UnitBankAccountReqRes.model';
import {UnitDepositAccountStatus} from '../../../enums/UnitDepositAccountStatus.enum';
import { UnitAccountLimits } from 'src/app/shared/models/unit-bank-account/UnitAccountLimits.model';


@Injectable()
export class UnitBankAccountService {

  private refreshedBankAccountDetails$ = new Subject<UnitBankAccountReqRes>();

  bankAccountId$ = this.activatedRoute.params.pipe(
    map(params => +params.id)
  );

  private bankAccountById$ = this.bankAccountId$.pipe(
    switchMap(id => this.unitBankAccountDataService.getById(id)),
    shareReplay(1)
  );

  bankAccount$ = merge(this.bankAccountById$, this.refreshedBankAccountDetails$)
    .pipe(
      shareReplay(1)
    );

  public filterCardTransactions$ = new Subject<string>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private unitBankAccountDataService: UnitBankAccountDataService
  ) {
  }

  /** Mark the CRE account as approved by the user, and trigger refresh of the account details. */
  approve() {
    return this.bankAccountId$.pipe(
      take(1),
      switchMap(id => this.unitBankAccountDataService.approve(id)),
      tap(_ => this.refreshAccountDetails()),
      map(_ => true)
    );
  }

  public getAccountBalance(bankAccountId: number): Observable<UnitBankAccountBalanceReqRes> {
    return this.unitBankAccountDataService.getBalanceAndTotalTransactions(bankAccountId);
  }

  public downloadBankAccountStatement(statementId: string, bankAccountId: string): any {
    return this.unitBankAccountDataService.getBankAccountStatement(statementId, bankAccountId);
  }

  public getBankAccountAchDebitLimit(bankAccountId: number): Observable<UnitAccountLimits> {
    return this.unitBankAccountDataService.getBankAccountAchLimits(bankAccountId);
  }

  public refreshAccountDetails() {
    return this.bankAccountId$.pipe(
      take(1),
      switchMap(id => this.unitBankAccountDataService.getById(id)),
      tap(updatedAccount => this.refreshedBankAccountDetails$.next(updatedAccount))
    ).subscribe();
  }

  public toggleDistributionUsage() {
    return this.bankAccountId$.pipe(
      take(1),
      switchMap(id => (this.unitBankAccountDataService as CreBankAccountDataService).toggleDistribution(id))
    );
  }

  public downloadFaqPdf(bankAccountId: number): Observable<any> {
    return this.unitBankAccountDataService.downloadFaqPdf(bankAccountId);
  }

  public generateVerificationLetter(includeBalance: boolean): Observable<any> {
    return this.bankAccountId$.pipe(
      switchMap(id => this.unitBankAccountDataService.generateVerificationLetter(id, includeBalance))
    );
  }

  public closeAccount(bankAccountId: number): Observable<boolean> {
    return this.unitBankAccountDataService.closeAccount(bankAccountId);
  }
}
